import { useState } from 'react'
import { Button, Card, IconButton, Person } from '../../../design/components'
import styles from '../../scenes/sections/play.module.css'
import { useCaseCandidates } from '../queries'
import type { CaseCharactersSection } from '../types'
import type { CaseEditing } from '../useCaseEditing'

/**
 * The characters the case turns on.
 *
 * Candidates come from `available-characters`, which the server has already narrowed to
 * the campaign roster minus whoever is attached — so this never offers somebody the API
 * would refuse and never subtracts one list from another.
 */
export function MajorCharactersCard({
  caseId,
  characters,
  editing,
}: {
  caseId: number
  characters: CaseCharactersSection | undefined
  editing: CaseEditing
}) {
  const [adding, setAdding] = useState(false)

  if (characters === undefined) return null

  const all = characters.characters ?? []

  return (
    <Card
      variant="skills"
      label="Major characters"
      hint={all.length === 1 ? '1 character' : `${all.length} characters`}
      action={
        editing.canEdit && !adding ? (
          <IconButton label="Add a major character" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && (
        <p className={styles.empty}>Nobody named as a major character yet.</p>
      )}

      {all.map((entry) => (
        <div key={entry.id} className={styles.row}>
          <div className={styles.rowHead}>
            <Person
              name={entry.name}
              portraitUrl={entry.portraitUrl}
              meta={String(entry.type)}
              characterId={entry.characterId}
            />
            {editing.canEdit && (
              <Button
                variant="danger"
                disabled={editing.removeCharacter.isPending}
                onClick={() => editing.removeCharacter.mutate({ characterId: entry.characterId })}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      ))}

      {editing.canEdit && adding && (
        <AddMajorCharacterForm caseId={caseId} editing={editing} onClose={() => setAdding(false)} />
      )}
    </Card>
  )
}

function AddMajorCharacterForm({
  caseId,
  editing,
  onClose,
}: {
  caseId: number
  editing: CaseEditing
  onClose: () => void
}) {
  const candidates = useCaseCandidates(caseId)
  const found = candidates.data ?? []

  return (
    <div className={styles.inlineForm}>
      {candidates.isPending && <p className={styles.note}>Loading the roster…</p>}

      {candidates.isSuccess && found.length === 0 && (
        <p className={styles.note}>Everyone on the campaign roster is already on this case.</p>
      )}

      <div className={styles.pickerList}>
        {found.map((candidate) => (
          <button
            key={candidate.characterId}
            type="button"
            className={styles.pickerRow}
            disabled={editing.addCharacter.isPending}
            onClick={() =>
              editing.addCharacter.mutate(
                { characterId: candidate.characterId ?? 0 },
                { onSuccess: onClose },
              )
            }
          >
            <Person
              name={candidate.name ?? ''}
              portraitUrl={candidate.portraitUrl}
              meta={String(candidate.type ?? '')}
            />
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
